import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Bookmark as BookmarkIcon, Check } from 'lucide-react';
import { BloggerPost } from '../services/mockData';
import { extractFirstImage, cn, formatBDDate } from '../lib/utils';
import { useBookmarks } from '../store/bookmarks';

interface NewsCardProps {
  post: BloggerPost;
  featured?: boolean;
  variant?: 'list' | 'list_no_image' | 'slider' | 'grid' | 'horizontal_hero';
  className?: string;
  isDarkTheme?: boolean;
}

const LazyImage = ({ src, alt, className, priority = false }: { src: string, alt: string, className?: string, priority?: boolean }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  if (priority) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn(className)}
        loading="eager"
        fetchPriority="high"
        decoding="sync"
      />
    );
  }

  return (
    <>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse" />
      )}
      <img
        src={src}
        alt={alt}
        className={cn(className, !isLoaded ? 'opacity-0' : 'opacity-100')}
        loading="lazy"
        fetchPriority="auto"
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
      />
    </>
  );
};

export const NewsCard: React.FC<NewsCardProps & { priority?: boolean }> = ({ post, featured = false, variant = 'list', className, priority = false }) => {
  const targetWidth = featured ? 1000 : 500;
  const targetHeight = featured ? 562 : 281;
  const imageUrl = extractFirstImage(post.content, targetWidth, targetHeight);
  const { toggleBookmark, isBookmarked } = useBookmarks();
  const bookmarked = isBookmarked(post.id);
  const [copied, setCopied] = useState(false);

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: 'সত্য প্রকাশে আপোষহীন - সেবা হট নিউজ। খবরটি পড়ুন:',
        url: `${window.location.origin}${post.url ? new URL(post.url).pathname : '/post/' + post.id}`,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(`${window.location.origin}${post.url ? new URL(post.url).pathname : '/post/' + post.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark(post);
  };

  if (featured) {
    return (
      <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={cn("block h-full min-h-[260px] w-full group relative rounded bg-[#111111] active:scale-[0.98] transition-transform duration-200 overflow-hidden shadow-sm", className)}>
        <div className="h-full w-full overflow-hidden relative bg-[#111111]">
          <LazyImage src={imageUrl} alt="" priority={priority} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent flex flex-col justify-end p-4 md:p-5 pointer-events-none">
            <div className="flex gap-2 mb-2 pointer-events-auto">
              {post.labels?.[0] && (
                <span className="bg-[#D32F2F] text-white text-[11px] md:text-[13px] font-bold px-2.5 py-1 rounded-sm tracking-wider shadow-sm">
                  {post.labels[0]}
                </span>
              )}
            </div>
            <h2 className="text-white text-[20px] font-bold leading-snug line-clamp-3 font-bengali pointer-events-auto drop-shadow-md">
              {post.title}
            </h2>
            <div className="text-gray-300 text-xs md:text-sm mt-2 flex items-center justify-between pointer-events-auto min-h-[44px]">
              <span>{formatBDDate(post.published, true)}</span>
              <div className="flex gap-1">
                 <button onClick={handleShare} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-white transition-colors" title="Share">
                   {copied ? <Check size={18} className="text-green-400" /> : <Share2 size={18} />}
                 </button>
                 <button onClick={handleBookmark} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-white transition-colors" title={bookmarked ? "Remove bookmark" : "Save for later"}>
                   <BookmarkIcon size={18} className={bookmarked ? "fill-white text-white" : ""} />
                 </button>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal_hero') {
    return (
      <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={cn("flex flex-col md:flex-row w-full group relative bg-white dark:bg-gray-950 active:scale-[0.98] transition-transform duration-200 overflow-hidden h-full rounded shadow-sm border border-gray-100 dark:border-gray-800", className)}>
        <div className="md:w-3/5 lg:w-2/3 aspect-[16/9] md:aspect-auto md:h-full overflow-hidden relative bg-gray-200 dark:bg-gray-800 shrink-0">
          <LazyImage src={imageUrl} alt="" priority={priority} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
        <div className="flex flex-col flex-1 p-4 md:p-6 lg:p-8">
          <div className="mb-4">
            {post.labels?.[0] && (
              <span className="inline-block bg-[#D32F2F]/10 text-[#D32F2F] font-bold text-xs px-2.5 py-1 rounded mb-3">
                {post.labels[0]}
              </span>
            )}
            <h2 className="font-bold text-[#111111] dark:text-gray-100 text-[20px] md:text-[24px] lg:text-[28px] leading-snug md:leading-snug line-clamp-3 font-bengali group-hover:text-[#D32F2F] transition-colors">
              {post.title}
            </h2>
          </div>
          
          <div className="text-gray-600 dark:text-gray-400 text-sm md:text-base line-clamp-3 md:line-clamp-4 font-bengali mb-4 flex-1">
            {/* simple preview text generation from raw content, stripping HTML */}
            {post.content.replace(/<[^>]*>?/gm, '').substring(0, 150)}...
          </div>
          
          <div className="mt-auto flex items-center justify-between border-t border-gray-100 dark:border-gray-800/60 pt-4">
            <div className="flex items-center text-sm text-[#666666] dark:text-gray-400 font-medium">
              <span>{formatBDDate(post.published, true)}</span>
            </div>
            <div className="flex items-center text-gray-500 dark:text-gray-500 relative z-10 gap-2">
               <button onClick={handleShare} className="p-2 flex items-center justify-center hover:text-[#D32F2F] transition-colors rounded-full hover:bg-gray-50 dark:hover:bg-gray-800" title="Share">
                 {copied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
               </button>
               <button onClick={handleBookmark} className="p-2 flex items-center justify-center hover:text-[#D32F2F] transition-colors rounded-full hover:bg-gray-50 dark:hover:bg-gray-800" title={bookmarked ? "Remove bookmark" : "Save for later"}>
                 <BookmarkIcon size={18} className={bookmarked ? "fill-[#D32F2F] text-[#D32F2F]" : ""} />
               </button>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'slider' || variant === 'grid') {
    return (
      <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={cn("block w-full group relative bg-white dark:bg-gray-950 active:scale-[0.98] transition-transform duration-200 overflow-hidden flex flex-col h-full", className)}>
        <div className="aspect-[16/9] w-full overflow-hidden relative bg-gray-200 dark:bg-gray-800 shrink-0 mb-3 rounded shadow-sm">
          <LazyImage src={imageUrl} alt="" priority={priority} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
        <div className="flex flex-col flex-1">
          <h3 className="font-bold text-[#111111] dark:text-gray-100 text-[15px] md:text-[17px] leading-snug md:leading-snug line-clamp-3 font-bengali group-hover:text-[#D32F2F] transition-colors mb-2">
            {post.title}
          </h3>
          <div className="mt-auto flex items-center justify-between">
            <div className="flex flex-wrap items-center text-[11px] md:text-xs text-[#666666] dark:text-gray-400">
              {post.labels?.[0] && (
                <span className="inline-block text-[#D32F2F] font-bold mr-2">
                  {post.labels[0]}
                </span>
              )}
              <span>{formatBDDate(post.published, false)}</span>
            </div>
            <div className="flex items-center text-gray-500 dark:text-gray-500 relative z-10">
               <button onClick={handleShare} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-[#D32F2F] transition-colors" title="Share">
                 {copied ? <Check size={15} className="text-green-500" /> : <Share2 size={15} />}
               </button>
               <button onClick={handleBookmark} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-[#D32F2F] transition-colors" title={bookmarked ? "Remove bookmark" : "Save for later"}>
                 <BookmarkIcon size={15} className={bookmarked ? "fill-[#D32F2F] text-[#D32F2F]" : ""} />
               </button>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'list' || variant === 'list_no_image') {
    return (
      <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={cn("flex gap-3 relative group active:scale-[0.98] transition-transform duration-200 pb-[5px] pt-0", className)}>
        {variant !== 'list_no_image' && (
          <div className="relative w-[100px] h-[56px] shrink-0 overflow-hidden rounded bg-gray-200 dark:bg-gray-800 shadow-sm">
            <LazyImage src={imageUrl} alt="" priority={priority} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
        )}
        <div className="flex flex-col justify-between flex-1 py-0">
          <div>
            <h3 className="font-bold text-[#111111] dark:text-[#EAEAEA] text-[13px] leading-snug line-clamp-3 font-bengali group-hover:text-[#D32F2F] dark:group-hover:text-[#D32F2F] transition-colors pt-[5px] mb-0">
              {post.title}
            </h3>
          </div>
          
          <div className="flex items-center justify-between mt-1">
            <div className="flex flex-wrap items-center text-[11px] text-[#666666] dark:text-gray-400">
              {post.labels?.[0] && (
                <span className="inline-block text-[#D32F2F] font-bold mr-1.5">
                  {post.labels[0]}
                </span>
              )}
              <span>{formatBDDate(post.published, false)}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return null;
};
